import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../config';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean; // Add loading state
  login: (data: { token: string; user: User }) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state synchronously from localStorage
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('find_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('find_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      console.log('Verifying token with /api/auth/me...');
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => {
        if (!res.ok) {
          console.warn('Token verification failed, clearing state. Status:', res.status);
          // Token invalid, clear state
          setUser(null);
          setToken(null);
          localStorage.removeItem('find_user');
          localStorage.removeItem('find_token');
        } else {
          console.log('Token verified successfully');
        }
      })
      .catch((err) => {
        console.error('Network error during token verification:', err);
        // Network error, keep current local state (optimistic)
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (data: { token: string; user: User }) => {
    setUser(data.user);
    setToken(data.token);
    localStorage.setItem('find_user', JSON.stringify(data.user));
    localStorage.setItem('find_token', data.token);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('find_user');
    localStorage.removeItem('find_token');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
